/**
 * The floating panel's header keeps its title readable at any width (0.15.0). Its buttons sit beside the title; when they
 * would leave the title less than HEADER_TITLE_MIN pixels, the ones that do not fit move into the "More actions" menu
 * (HeaderOverflowMenu), lowest priority first. The Close button never moves. The title itself always ends in an ellipsis
 * inside its own box (gyro.css), so it never sits under a control.
 *
 * headerOverflow is the pure decision; measureHeaderOverflow reads a laid-out header and feeds it. Nothing is measured where
 * nothing is laid out (a server render, jsdom, a hidden panel): then every button stays where it is.
 */
/** The least room the chat title keeps beside the header's buttons: "New chat" and its rename pencil, or a short title over "Here to help". */
export const HEADER_TITLE_MIN = 112;
/**
 * Which header buttons may move, and in what order (lowest first). The ids are the `data-header-action` values the header's
 * buttons carry; a button without an entry never moves. The workspace look has Fork chat, New chat, Commands, Expand and
 * Close; the compact look adds Capabilities, Memory and Chat history.
 *
 * The rule for Fork chat: it ranks between Chat history and Expand. In a crowded header it waits in the More menu before
 * Commands, New chat and Expand do, because the command palette offers Fork chat too and the menu keeps it one click away;
 * the three it yields to have no second home in the header. The compact look at 400 px (The Lab) is the case that shows it:
 * Capabilities, Memory, Chat history and Fork chat wait in the menu while Commands, New chat, Expand and Close stay.
 */
export const HEADER_ACTION_PRIORITY = {
    capabilities: 10,
    memory: 20,
    history: 30,
    'fork-chat': 35,
    expand: 40,
    commands: 50,
    'new-chat': 60,
    close: Number.POSITIVE_INFINITY,
};
/**
 * The ids of the buttons that move into the "More actions" menu, in header order. None when every button fits, when nothing
 * was measured (a button 0 wide), or when fewer than two could move: the More button takes a place of its own, so moving one
 * button frees nothing.
 */
export function headerOverflow({ available, button, gap, actions }) {
    if (!(button > 0) || !Number.isFinite(available) || actions.length === 0)
        return [];
    const spacing = Math.max(0, Number.isFinite(gap) ? gap : 0);
    const fits = Math.max(0, Math.floor((available + spacing) / (button + spacing)));
    if (actions.length <= fits)
        return [];
    const pinned = actions.filter((action) => action.priority === Number.POSITIVE_INFINITY).length;
    if (actions.length - pinned < 2)
        return [];
    // The buttons that stay: as many as fit beside the More button, at least the pinned ones, and never all but one.
    const keep = Math.max(pinned, Math.min(fits - 1, actions.length - 2));
    const ranked = actions.map((action, index) => ({ action, index })).sort((a, b) => b.action.priority - a.action.priority || a.index - b.index);
    const kept = new Set(ranked.slice(0, keep).map(({ action }) => action.id));
    return actions.filter((action) => !kept.has(action.id)).map((action) => action.id);
}
const px = (value) => {
    const number = Number.parseFloat(value ?? '');
    return Number.isFinite(number) ? number : 0;
};
/**
 * Measure a laid-out header (`.gyro-head`, whose buttons are in `.gyro-row`) and decide which buttons move. Every button that
 * carries a `data-header-action` with a priority counts, moved or not; the Close button gives the width of one. The navigation
 * toggle (the workspace look) or the avatar (the compact look) stays beside the title. Never throws: anything unreadable
 * moves nothing.
 */
export function measureHeaderOverflow(header) {
    try {
        const view = header?.ownerDocument?.defaultView;
        const row = header?.querySelector(':scope > .gyro-row');
        if (!header || !view || !row)
            return [];
        const style = view.getComputedStyle(header);
        const inner = header.clientWidth - px(style.paddingLeft) - px(style.paddingRight);
        const headGap = px(style.columnGap);
        const toggle = header.querySelector(':scope > .gyro-desktop-sidebar-toggle');
        const avatar = header.querySelector(':scope > .gyro-title > .gyro-head-avatar');
        const lead = toggle
            ? toggle.getBoundingClientRect().width + headGap
            : avatar ? avatar.getBoundingClientRect().width + px(view.getComputedStyle(avatar.parentElement).columnGap) : 0;
        const close = row.querySelector(':scope > [data-header-action="close"]');
        // A button the stylesheet hides (Expand on a full-screen panel) takes no room and has no place in the menu.
        const actions = Array.from(row.querySelectorAll(':scope > [data-header-action]'))
            .filter((element) => element.hasAttribute('data-header-overflowed') || view.getComputedStyle(element).display !== 'none')
            .map((element) => element.dataset.headerAction ?? '')
            .filter((id) => id in HEADER_ACTION_PRIORITY)
            .map((id) => ({ id, priority: HEADER_ACTION_PRIORITY[id] }));
        return headerOverflow({
            available: inner - lead - HEADER_TITLE_MIN - headGap,
            button: close ? close.getBoundingClientRect().width : 0,
            gap: px(view.getComputedStyle(row).columnGap),
            actions,
        });
    }
    catch {
        return [];
    }
}
//# sourceMappingURL=headerOverflow.js.map